import { DampersState, DamperData } from "models";
import { initialState } from "../initialState";
import {
  DampersActions,
  DampersActionTypes,
  FetchingDamperConfigsAction,
  SetDamperConfigsAction,
  FetchingDamperConfigsFailedAction,
  UpdateDamperValueAction,
  ToggleOverrideAllAction,
} from "./Dampers.actions";
import { validateDamperData, setTagValidity, setTagProperty } from "utils/dampers.utils";

const setFetchingStatus = (state: boolean, action: FetchingDamperConfigsAction): boolean => {
  switch (action.type) {
    case DampersActions.SetDamperConfigs:
    case DampersActions.FetchingDamperConfigsFailed:
      return false;
    case DampersActions.FetchingDamperConfigs:
      return true;
    default:
      return state;
  }
};

const setDamperData = (state: DamperData[] = [], action: SetDamperConfigsAction | UpdateDamperValueAction): DamperData[] => {
  switch (action.type) {
    case DampersActions.UpdateDamperValue:
      const { type, tag_name, update } = (action as UpdateDamperValueAction).payload;

      return state.map((damper) => {
        if (damper.type === type) {
          return {
            ...damper,
            tags: damper.tags.map((tag) => {
              if (tag.tag_name === tag_name) {
                return setTagValidity({ ...tag, ...update });
              }

              if (tag.tags) {
                return {
                  ...tag,
                  tags: tag.tags.map((localTag) =>
                    localTag.tag_name === tag_name ? setTagValidity({ ...localTag, ...{ ...update } }) : localTag,
                  ),
                };
              }

              return setTagValidity(tag);
            }),
          };
        }

        return damper;
      });

    case DampersActions.SetDamperConfigs:
      const incomingData: DamperData[] = [...(action as SetDamperConfigsAction).payload.damperData];
      const reload: boolean = (action as SetDamperConfigsAction).payload.reloadDampers;

      if (!state.length || reload) return validateDamperData(incomingData);

      return state.map((damper, damperIndex) => {
        return {
          ...damper,
          tags: damper.tags.map((tag, tagIndex) => {
            if (tag.tags) {
              return {
                ...tag,
                tags: tag.tags.map((localTag, localTagIndex) => {
                  const damper = { ...incomingData[damperIndex] };
                  const tag = { ...damper.tags[tagIndex] };
                  const incomingLocalTag = tag.tags ? tag.tags[localTagIndex] : localTag;

                  return setTagValidity({ ...localTag, ...incomingLocalTag });
                }),
              };
            }

            return setTagValidity({ ...tag, ...incomingData[damperIndex].tags[tagIndex] });
          }),
        };
      });

    case DampersActions.FetchingDamperConfigsFailed:
      return [];

    default:
      return state;
  }
};

const fetchingFailed = (state: boolean = false, action: FetchingDamperConfigsFailedAction) => {
  switch (action.type) {
    case DampersActions.FetchingDamperConfigsFailed:
      return true;
    case DampersActions.SetDamperConfigs:
    case DampersActions.FetchingDamperConfigs:
      return false;
    default:
      return state;
  }
};

const setFetchTime = (state: string = "", action: FetchingDamperConfigsAction): string => {
  const fetchTime = action.payload?.fetchTime || "";

  switch (action.type) {
    case DampersActions.FetchingDamperConfigs:
      return fetchTime;
    default:
      return state;
  }
};

const setFetchLine = (state: string | "", action: FetchingDamperConfigsAction): string => {
  switch (action.type) {
    case DampersActions.FetchingDamperConfigs:
      return action.payload.fetchLineId;
    default:
      return state;
  }
};

export const dampersReducer = (state: DampersState = initialState.dampers, action: DampersActionTypes): DampersState => {
  switch (action.type) {
    case DampersActions.ToggleOverrideAll:
      const toggle = (action as ToggleOverrideAllAction).payload;
      const newDampers = setTagProperty(state.data, { override: toggle });
      return { ...state, data: newDampers, overrideAll: toggle };
    default:
      return {
        fetching: setFetchingStatus(state.fetching, action as FetchingDamperConfigsAction),
        didInvalidate: fetchingFailed(state.didInvalidate, action as FetchingDamperConfigsFailedAction),
        fetchTime: setFetchTime(state.fetchTime, action as FetchingDamperConfigsAction),
        fetchLine: setFetchLine(state.fetchLine, action as FetchingDamperConfigsAction),
        overrideAll: state.overrideAll,
        data: setDamperData(state.data, action as SetDamperConfigsAction | UpdateDamperValueAction),
      };
  }
};
